import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSocket } from "../../utils/socket";
import { motion } from "framer-motion";

// Hàm tiện ích để ánh xạ trạng thái với màu sắc
const getStatusStyles = (status) => {
  switch (status) {
    case "approved":
      return { backgroundColor: "bg-green-500", textColor: "text-white" };
    case "pending":
      return { backgroundColor: "bg-yellow-500", textColor: "text-white" };
    case "rejected":
      return { backgroundColor: "bg-red-500", textColor: "text-white" };
    default:
      return { backgroundColor: "bg-gray-500", textColor: "text-white" };
  }
};

const AdminCoinWithdrawals = () => {
  const { fetchWithRetry, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState(null);

  // State cho tìm kiếm, lọc, sắp xếp, phân trang
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMethod, setFilterMethod] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Tải danh sách yêu cầu rút tiền
  const loadWithdrawalRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetchWithRetry({ method: "get", url: "/api/admin/coins/withdrawal-requests" });

      console.log("Withdrawal Requests Data:", res.data);

      const requestsData = res.data.requests || [];
      setRequests(requestsData);
      setFilteredRequests(requestsData);

      if (requestsData.length === 0) {
        setError("Không có yêu cầu rút tiền nào được tìm thấy.");
      }
    } catch (error) {
      console.error("Lỗi khi tải danh sách yêu cầu rút tiền:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi tải danh sách yêu cầu rút tiền. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError("Không thể tải danh sách yêu cầu rút tiền!");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry]);

  // Xử lý phê duyệt hoặc từ chối yêu cầu rút tiền
  const handleRequestAction = async (requestId, action) => {
    if (!window.confirm(`Bạn có chắc muốn ${action === "approve" ? "phê duyệt" : "từ chối"} yêu cầu này?`)) return;
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/coins/handle-withdrawal-request",
        data: { requestId, action },
      });
      setNotificationMessage(response.data.message || `${action === "approve" ? "Phê duyệt" : "Từ chối"} yêu cầu thành công!`);
      loadWithdrawalRequests();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      console.error(`Lỗi khi ${action === "approve" ? "phê duyệt" : "từ chối"} yêu cầu rút tiền:`, error);
      if (error.message.includes("500")) {
        setError(`Lỗi server khi ${action === "approve" ? "phê duyệt" : "từ chối"} yêu cầu. Vui lòng thử lại sau!`);
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError(error.response?.data?.message || `Không thể ${action === "approve" ? "phê duyệt" : "từ chối"} yêu cầu này!`);
      }
    }
  };

  // Lọc, sắp xếp yêu cầu rút tiền
  useEffect(() => {
    let filtered = [...requests];

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (request) =>
          request.userId?.name?.toLowerCase().includes(lowerSearchTerm) ||
          request.paymentMethod.toLowerCase().includes(lowerSearchTerm) ||
          request.paymentAccount.toLowerCase().includes(lowerSearchTerm)
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((request) => request.status === filterStatus);
    }

    if (filterMethod) {
      filtered = filtered.filter((request) => request.paymentMethod === filterMethod);
    }

    filtered.sort((a, b) => {
      const fieldA = a[sortField] || "";
      const fieldB = b[sortField] || "";
      if (sortField === "createdAt" || sortField === "updatedAt") {
        return sortOrder === "asc" ? new Date(fieldA) - new Date(fieldB) : new Date(fieldB) - new Date(fieldA);
      } else if (sortField === "coinAmount" || sortField === "cashAmount") {
        return sortOrder === "asc" ? fieldA - fieldB : fieldB - fieldA;
      } else {
        return sortOrder === "asc"
          ? fieldA.toString().localeCompare(fieldB.toString())
          : fieldB.toString().localeCompare(fieldA.toString());
      }
    });

    setFilteredRequests(filtered);
  }, [requests, searchTerm, filterStatus, filterMethod, sortField, sortOrder]);

  // Phân trang
  const totalItems = filteredRequests.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = filteredRequests.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Kiểm tra quyền truy cập và tải dữ liệu
  useEffect(() => {
    if (isLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }
    loadWithdrawalRequests();

    const socket = getSocket();
    socket.on("notification", (notification) => {
      if (notification.type === "withdrawal_request" || notification.type === "withdrawal_status") {
        setNotificationMessage(`Yêu cầu rút tiền mới đã được cập nhật: ${notification.message}`);
        loadWithdrawalRequests();
        setTimeout(() => setNotificationMessage(null), 3000);
      }
    });

    return () => {
      socket.off("notification");
    };
  }, [user, navigate, loadWithdrawalRequests, isLoading]);

  if (loading) return <p className="text-center text-gray-500">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Quản lý yêu cầu rút tiền</h2>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          {error.includes("Phiên làm việc hết hạn") ? (
            <button
              onClick={() => navigate("/login")}
              className="ml-4 px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
            >
              Đăng nhập lại
            </button>
          ) : (
            <button
              onClick={() => setError(null)}
              className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
            >
              Đóng
            </button>
          )}
        </div>
      )}

      {notificationMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{notificationMessage}</div>
      )}

      {filteredRequests.length === 0 && !error && !loading && (
        <p className="text-center text-gray-500">Không có yêu cầu rút tiền nào được tìm thấy.</p>
      )}

      {filteredRequests.length > 0 && (
        <>
          <div className="mb-6 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Tìm kiếm theo tên người dùng, phương thức, tài khoản..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Lọc theo trạng thái</option>
                <option value="pending">Đang chờ</option>
                <option value="approved">Đã phê duyệt</option>
                <option value="rejected">Đã từ chối</option>
              </select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <select
                value={filterMethod}
                onChange={(e) => setFilterMethod(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Lọc theo phương thức</option>
                <option value="paypal">Paypal</option>
                <option value="bitcoin">Bitcoin</option>
                <option value="usdt">USDT</option>
                <option value="bank">Bank</option>
              </select>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="overflow-x-auto"
          >
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="bg-blue-500 text-white">
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("userId.name")}>
                    Người dùng {sortField === "userId.name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("coinAmount")}>
                    Số Coin {sortField === "coinAmount" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("cashAmount")}>
                    Số tiền (VND) {sortField === "cashAmount" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("paymentMethod")}>
                    Phương thức {sortField === "paymentMethod" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("paymentAccount")}>
                    Tài khoản {sortField === "paymentAccount" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("status")}>
                    Trạng thái {sortField === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("createdAt")}>
                    Ngày tạo {sortField === "createdAt" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("updatedAt")}>
                    Ngày cập nhật {sortField === "updatedAt" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left">Hành động</th>
                </tr>
              </thead>
              <tbody>
                {currentRequests.map((request) => {
                  const { backgroundColor, textColor } = getStatusStyles(request.status);

                  return (
                    <tr key={request._id} className="border-b hover:bg-gray-50 transition duration-200">
                      <td className="p-4">{request.userId?.name || "N/A"}</td>
                      <td className="p-4">{request.coinAmount}</td>
                      <td className="p-4">{request.cashAmount.toLocaleString()}</td>
                      <td className="p-4">{request.paymentMethod}</td>
                      <td className="p-4">{request.paymentAccount}</td>
                      <td className="p-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full ${backgroundColor} ${textColor}`}
                        >
                          {request.status === "pending"
                            ? "Đang chờ"
                            : request.status === "approved"
                            ? "Đã phê duyệt"
                            : "Đã từ chối"}
                        </span>
                      </td>
                      <td className="p-4">{new Date(request.createdAt).toLocaleString("vi-VN")}</td>
                      <td className="p-4">{new Date(request.updatedAt).toLocaleString("vi-VN")}</td>
                      <td className="p-4">
                        {request.status === "pending" && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleRequestAction(request._id, "approve")}
                              className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600"
                            >
                              Phê duyệt
                            </button>
                            <button
                              onClick={() => handleRequestAction(request._id, "reject")}
                              className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              Từ chối
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>

          <div className="mt-6 flex justify-between items-center">
            <div>
              <label className="mr-2">Hiển thị:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="p-2 border border-gray-300 rounded-lg"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="ml-2">mục/trang</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Trước
              </button>
              <span className="px-4 py-1">
                Trang {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Sau
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminCoinWithdrawals;